import { PRODUCT_CATEGORY_GROUPS } from './productNavigation';

const WORD_BOUNDARY_PHRASES = [
  'Hippo',
  'South Africa',
  'SA',
  'RSA',
  'ZA',
  'OTP',
  'SMS',
  'FAQ',
  'FAQs',
  'CTA',
  'API',
  'GPS',
  'MUI',
];

const PRODUCT_PHRASES = PRODUCT_CATEGORY_GROUPS.flatMap(
  ({ title, links = [], launchPadOptions = [] }) => [
    title,
    ...links.map(({ label }) => label),
    ...launchPadOptions,
  ]
);

const KNOWN_PHRASES = Array.from(
  new Set([
    ...WORD_BOUNDARY_PHRASES,
    ...PRODUCT_PHRASES.filter((phrase) => /[\s&/-]/.test(phrase)),
  ])
).filter(Boolean);

const PRESERVED_TITLE_CASE_WORDS = new Map(
  WORD_BOUNDARY_PHRASES.filter((phrase) => !phrase.includes(' ')).map(
    (phrase) => [phrase.toLowerCase(), phrase]
  )
);

const normalizeWhitespace = (value) =>
  String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isAllCapsAcronym = (value) =>
  /^[A-Z0-9&.-]+$/.test(value) &&
  value === value.toUpperCase() &&
  value.length <= 6;

const titleCaseWord = (word) => {
  if (!word) return word;
  if (isAllCapsAcronym(word)) return word;

  const preservedWord = PRESERVED_TITLE_CASE_WORDS.get(word.toLowerCase());
  if (preservedWord) return preservedWord;

  return word
    .toLowerCase()
    .replace(
      /(^|[-/(\s])([a-z])/g,
      (match, prefix, char) => `${prefix}${char.toUpperCase()}`
    )
    .replace(/^([a-z])/, (match) => match.toUpperCase());
};

export const toTitleCase = (value = '') => {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return '';

  return normalized
    .split(/(\s+)/)
    .map((segment) => {
      if (!segment.trim()) return segment;
      if (segment === '&') return segment;
      return segment
        .split('-')
        .map((part) => titleCaseWord(part))
        .join('-');
    })
    .join('');
};

const restoreKnownPhrases = (text) => {
  let next = text;
  const phrases = [...KNOWN_PHRASES].sort((a, b) => b.length - a.length);

  for (const phrase of phrases) {
    const pattern = new RegExp(
      `(^|[^A-Za-z0-9])(${escapeRegex(phrase.toLowerCase())})(?=$|[^A-Za-z0-9])`,
      'gi'
    );
    next = next.replace(pattern, (match, prefix) => `${prefix}${phrase}`);
  }

  return next;
};

const restoreCurrencyPrefixes = (text) => text.replace(/\br(?=\s*\d)/g, 'R');

const restorePronouns = (text) =>
  text
    .replace(/\bi(['’](?:m|d|ll|ve|re|s|t))\b/g, 'I$1')
    .replace(/\bi\b(?!['’]|\.[a-z])/g, 'I');

export const toSentenceCase = (value = '') => {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return '';

  const lowerCased = normalized.toLowerCase();
  const sentenceCased = lowerCased.replace(
    /(^|[.!?]\s+)([a-z])/g,
    (match, prefix, char) => `${prefix}${char.toUpperCase()}`
  );

  return restorePronouns(
    restoreCurrencyPrefixes(restoreKnownPhrases(sentenceCased))
  );
};
